import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Collects the section ids of every cart-items-component on the page so they all re-render.
 * @param {string} [sectionId]
 * @returns {string[]}
 */
function getCartSectionIds(sectionId) {
  const ids = new Set(sectionId ? [sectionId] : []);
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
  }
  return [...ids];
}

/** Cart attribute that remembers a customer switched insurance off, so it isn't re-added by default. */
const DECLINED_ATTRIBUTE = '_shipping_insurance_declined';

/** Prevents duplicate automatic adds while a request is in flight or after one has failed. */
let autoAddAttempted = false;

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Adds or removes the shipping insurance line and dispatches a cart update so the drawer,
 * cart page and header bubble re-render through the theme's normal update path.
 * @param {HTMLElement} source - Element the event is dispatched from.
 * @param {{ add: boolean, variantId?: string, lineKey?: string, sectionId?: string, rememberChoice?: boolean, silent?: boolean }} options
 *   `rememberChoice` records (or clears) the declined cart attribute, used when insurance is on by default.
 *   `silent` reports the change as an update rather than an add, so the cart drawer doesn't auto-open.
 */
export async function updateInsurance(
  source,
  { add, variantId, lineKey, sectionId, rememberChoice = false, silent = false }
) {
  const sections = getCartSectionIds(sectionId);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: silent ? 'update' : add ? 'add' : 'remove',
      context: 'cart',
      lines: silent
        ? []
        : [add ? { merchandiseId: String(variantId), quantity: 1 } : { id: String(lineKey), quantity: 0 }],
      promise: deferred.promise,
    })
  );

  try {
    const sectionParams = { sections: sections.join(','), sections_url: location.pathname };

    if (add && rememberChoice) {
      await fetch(
        `${Theme.routes.cart_update_url}.js`,
        fetchConfig('json', { body: JSON.stringify({ attributes: { [DECLINED_ATTRIBUTE]: '' } }) })
      );
    }

    let url;
    let body;
    if (add) {
      url = Theme.routes.cart_add_url;
      body = { items: [{ id: Number(variantId), quantity: 1 }], ...sectionParams };
    } else if (rememberChoice) {
      url = `${Theme.routes.cart_update_url}.js`;
      body = { updates: { [String(lineKey)]: 0 }, attributes: { [DECLINED_ATTRIBUTE]: 'true' }, ...sectionParams };
    } else {
      url = Theme.routes.cart_change_url;
      body = { id: lineKey, quantity: 0, ...sectionParams };
    }

    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    const data = await response.json();
    if (data.status || data.errors) throw new Error(data.description || data.message || data.errors);

    const cart = add ? await fetchCart() : data;

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: data.sections,
        items: cart.items,
        source: 'cart-insurance-toggle',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Shipping insurance switch shown in the cart drawer summary.
 */
class CartInsuranceToggle extends HTMLElement {
  static observedAttributes = ['data-auto-add'];

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#maybeAutoAdd();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#maybeAutoAdd();
  }

  /** Adds insurance when it's on by default, the cart has products and the customer hasn't declined it. */
  async #maybeAutoAdd() {
    if (this.dataset.autoAdd !== 'true' || autoAddAttempted) return;
    autoAddAttempted = true;

    const { variantId, sectionId } = this.dataset;
    this.setAttribute('aria-busy', 'true');

    try {
      await updateInsurance(this, { add: true, variantId, sectionId, silent: true });
      // Allow a later auto-add (e.g. the cart is emptied and refilled) once this one has landed.
      autoAddAttempted = false;
    } catch (error) {
      console.error('[cart-insurance] Failed to add shipping insurance by default', error);
    } finally {
      this.removeAttribute('aria-busy');
      const input = this.#switch;
      if (input) input.checked = Boolean(this.dataset.lineKey);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  get #switch() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="checkbox"]'));
  }

  /** @param {Event} event */
  #onChange = async (event) => {
    const input = this.#switch;
    if (!input || event.target !== input) return;

    const add = input.checked;
    const { variantId, lineKey, sectionId } = this.dataset;
    if (!add && !lineKey) return;

    const rememberChoice = this.dataset.defaultOn === 'true';
    this.setAttribute('aria-busy', 'true');

    try {
      await updateInsurance(this, { add, variantId, lineKey, sectionId, rememberChoice });
    } catch (error) {
      console.error('[cart-insurance] Failed to update shipping insurance', error);
      input.checked = !add;
    } finally {
      this.removeAttribute('aria-busy');
      // Morphing updates the attribute but not the live `checked` property.
      const updated = this.#switch;
      if (updated) updated.checked = Boolean(this.dataset.lineKey);
    }
  };
}

/**
 * Rendered when the cart contains only the insurance line (every product was removed).
 * Removes the orphaned insurance so the customer isn't charged for protecting nothing.
 */
class CartInsuranceCleanup extends HTMLElement {
  connectedCallback() {
    const { lineKey, sectionId } = this.dataset;
    if (!lineKey || this.dataset.pending) return;
    this.dataset.pending = 'true';

    updateInsurance(this, { add: false, lineKey, sectionId, silent: true }).catch((error) =>
      console.error('[cart-insurance] Failed to remove orphaned shipping insurance', error)
    );
  }
}

if (!customElements.get('cart-insurance-toggle')) {
  customElements.define('cart-insurance-toggle', CartInsuranceToggle);
}

if (!customElements.get('cart-insurance-cleanup')) {
  customElements.define('cart-insurance-cleanup', CartInsuranceCleanup);
}
